import { parseCommunityAdminSection } from "./routing";

export type OAuthConnectionProvider = "x" | "discord";

export type OAuthConnectionStatus =
  | "success"
  | "error"
  | "server_selection_required";

export type OAuthConnectionEvent = {
  id: string;
  provider: OAuthConnectionProvider;
  status: OAuthConnectionStatus;
  communityId?: string;
  username?: string;
  reason?: string;
  selectionToken?: string;
  occurredAt: number;
};

const CHANNEL_NAME = "bbb.oauth.connection";
const STORAGE_KEY = "bbb.oauth.connection.latest";

function buildEventId() {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    return crypto.randomUUID();
  }

  return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

function parseEventPayload(payload: unknown): OAuthConnectionEvent | null {
  const parsed =
    typeof payload === "string"
      ? safeJsonParse(payload)
      : payload;

  if (!parsed || typeof parsed !== "object") {
    return null;
  }

  const event = parsed as Partial<OAuthConnectionEvent>;
  if (
    typeof event.id !== "string" ||
    (event.provider !== "x" && event.provider !== "discord") ||
    (
      event.status !== "success" &&
      event.status !== "error" &&
      event.status !== "server_selection_required"
    )
  ) {
    return null;
  }

  return {
    id: event.id,
    provider: event.provider,
    status: event.status,
    communityId: typeof event.communityId === "string" ? event.communityId : undefined,
    username: typeof event.username === "string" ? event.username : undefined,
    reason: typeof event.reason === "string" ? event.reason : undefined,
    selectionToken: typeof event.selectionToken === "string" ? event.selectionToken : undefined,
    occurredAt: typeof event.occurredAt === "number" ? event.occurredAt : Date.now(),
  };
}

function safeJsonParse(payload: string) {
  try {
    return JSON.parse(payload);
  } catch {
    return null;
  }
}

export function getCommunityIdFromOAuthReturnPath(pathname: string) {
  return parseCommunityAdminSection(pathname).communityId;
}

export function broadcastOAuthConnectionEvent(
  payload: Omit<OAuthConnectionEvent, "id" | "occurredAt">,
) {
  if (typeof window === "undefined") {
    return;
  }

  const event: OAuthConnectionEvent = {
    ...payload,
    id: buildEventId(),
    occurredAt: Date.now(),
  };

  if ("BroadcastChannel" in window) {
    const channel = new BroadcastChannel(CHANNEL_NAME);
    channel.postMessage(event);
    channel.close();
  }

  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(event));
    window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    // Best-effort fallback for browsers where BroadcastChannel is unavailable.
  }
}

export function subscribeToOAuthConnectionEvents(
  handler: (event: OAuthConnectionEvent) => void,
) {
  if (typeof window === "undefined") {
    return () => {};
  }

  const seen = new Set<string>();
  const handleEvent = (payload: unknown) => {
    const event = parseEventPayload(payload);
    if (!event || seen.has(event.id)) {
      return;
    }
    seen.add(event.id);
    handler(event);
  };

  let channel: BroadcastChannel | null = null;
  if ("BroadcastChannel" in window) {
    channel = new BroadcastChannel(CHANNEL_NAME);
    channel.onmessage = (message) => handleEvent(message.data);
  }

  const handleStorage = (storageEvent: StorageEvent) => {
    if (storageEvent.key === STORAGE_KEY && storageEvent.newValue) {
      handleEvent(storageEvent.newValue);
    }
  };

  window.addEventListener("storage", handleStorage);

  return () => {
    channel?.close();
    window.removeEventListener("storage", handleStorage);
  };
}

